import React, { useRef, useState, useEffect } from 'react';
import {
  Box,
  Heading,
  Text,
  VStack,
  FormControl,
  FormLabel,
  Input,
  Button,
  useToast,
  HStack,
  Select,
  Checkbox,
  Divider,
  Textarea,
  Alert,
  AlertIcon
} from '@chakra-ui/react';
import SignatureCanvas from 'react-signature-canvas';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import jsPDF from 'jspdf';

export default function AgreementSign() {
  // Basic info
  const [companyName, setCompanyName] = useState('');
  const [printedName, setPrintedName] = useState('');
  const [signDate, setSignDate] = useState('');
  const [effectiveDate, setEffectiveDate] = useState('');
  
  // Agreement terms
  const [perDrawFee, setPerDrawFee] = useState('');
  const [paymentFrequency, setPaymentFrequency] = useState('monthly');
  const [paymentMethod] = useState('ACH'); // Fixed to ACH only
  
  // Bank information
  const [bankAccountName, setBankAccountName] = useState('');
  const [bankName, setBankName] = useState('');
  const [bankRoutingNumber, setBankRoutingNumber] = useState('');
  const [bankAccountNumber, setBankAccountNumber] = useState('');
  const [bankAccountType, setBankAccountType] = useState('checking');
  
  // Compliance
  const [complianceAcknowledged, setComplianceAcknowledged] = useState(false);
  const [baaSigned, setBaaSigned] = useState(false);
  const [fmvCertified, setFmvCertified] = useState(false);
  const [stateAddendum, setStateAddendum] = useState('');
  
  // Other
  const [comments, setComments] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const sigPad = useRef();
  const toast = useToast();
  const navigate = useNavigate();
  const { id } = useParams();
  const [isSigned, setIsSigned] = useState(false);
  const [phlebotomistInfo, setPhlebotomistInfo] = useState(null);
  const [agreementText, setAgreementText] = useState('');

  // New detailed agreement template
  const defaultPhlebotomistTemplate = `PHLEBOTOMIST SERVICE AGREEMENT

This Phlebotomist Service Agreement ("Agreement") is made and entered into as of {EFFECTIVE_DATE} by and between At Home Blood Draw Inc ("Company") and the undersigned Phlebotomist / Phlebotomist ("Phlebotomist").

1. Services
Phlebotomist, as an independent contractor, shall collect patient specimens in accordance with valid physician orders and deliver them to the designated laboratory. Phlebotomist provides only collection services and shall not order, interpret, or promote laboratory tests.

2. Compensation
Fee: {PER_DRAW_FEE} per specimen collected, reflecting fair market value.
Payment: Monthly via ACH to the account designated by Phlebotomist. Payments are pass-through from the ordering laboratory.

3. Compliance & Conduct
Phlebotomist will comply with all applicable federal and state laws, including Anti-Kickback, Stark Law, HIPAA, and state licensing requirements.
Phlebotomist shall not solicit or influence laboratory selection.
Billing patients or insurers for compensated services is prohibited unless expressly authorized in writing by the Company.

4. Independent Contractor
Phlebotomist operates as an independent contractor and is responsible for taxes, insurance, licensure, and business expenses.

5. Confidentiality & Records
Phlebotomist shall protect Protected Health Information ("PHI") per HIPAA, governed by a separate Business Associate Agreement ("BAA").
Accurate collection records must be maintained and made available to the Company upon request.

6. Indemnification & Insurance
Phlebotomist agrees to indemnify and hold harmless the Company from any losses arising from Phlebotomist's negligence, misconduct, or legal violations.
Phlebotomist shall maintain professional liability insurance of at least $1,000,000 per occurrence / $3,000,000 aggregate and provide proof upon request.

7. Termination
This Agreement continues until terminated by either party with 30 days' written notice. Immediate termination is permitted for cause or if required licensure is lost.

8. State Addenda
Phlebotomist acknowledges that additional state-specific provisions may apply.

9. Entire Agreement
This Agreement, together with any executed BAA and state addenda, constitutes the entire understanding between the parties and supersedes all prior discussions.

Attachments:
Exhibit A – Business Associate Agreement (HIPAA)
Exhibit B – Fair Market Value Certification
Exhibit C – State Addendum (if required)`;

  useEffect(() => {
    const fetchPhlebotomistInfo = async () => {
      if (!id) return;
      const { data, error } = await supabase
        .from('phlebotomist_profiles')
        .select('*, current_agreement_id')
        .eq('id', id)
        .single();
      if (error) {
        toast({ title: 'Error loading phlebotomist info', description: error.message, status: 'error' });
        return;
      }
      setPhlebotomistInfo(data);
      if (data?.company_name) setCompanyName(data.company_name);
      if (data?.full_name) setBankAccountName(data.full_name);
      
      // Always use lab_draw_fee from profile (set by admin) for new agreements
      // This ensures new agreements use the current admin-set fee, not the old agreement's fee
      if (data?.lab_draw_fee !== null && data?.lab_draw_fee !== undefined) {
        setPerDrawFee(data.lab_draw_fee.toString());
      } else {
        setPerDrawFee('0.00');
      }
      
      // Set default effective date to today
      const today = new Date().toISOString().split('T')[0];
      setEffectiveDate(today);
      setSignDate(today);
    };
    fetchPhlebotomistInfo();
  }, [id, toast]);

  // Update agreement text when form fields change
  useEffect(() => {
    if (phlebotomistInfo) {
      const storedTemplate = localStorage.getItem('phlebotomistAgreementTemplate');
      const template = storedTemplate || defaultPhlebotomistTemplate;
      
      const formattedText = template
        .replace(/{PER_DRAW_FEE}/g, perDrawFee ? `$${parseFloat(perDrawFee).toFixed(2)}` : '$____')
        .replace(/{EFFECTIVE_DATE}/g, effectiveDate || '____');

      setAgreementText(formattedText);
    }
  }, [phlebotomistInfo, perDrawFee, effectiveDate]);

  const handleSign = async () => {
    if (!phlebotomistInfo || !phlebotomistInfo.id) {
      toast({ title: 'Error', description: 'Phlebotomist information not found', status: 'error' });
      return;
    }

    // Validation
    if (!companyName || !printedName || !signDate || !effectiveDate || !isSigned) {
      toast({ title: 'Missing required fields', description: 'Please fill all required fields and sign', status: 'error' });
      return;
    }

    if (!perDrawFee || parseFloat(perDrawFee) <= 0) {
      toast({ 
        title: 'Fee not set', 
        description: 'Please contact administrator to set your per-draw fee before signing the agreement', 
        status: 'error',
        duration: 5000
      });
      return;
    }

    // Bank information is always required since payment method is fixed to ACH
    if (!bankAccountName || !bankName || !bankRoutingNumber || !bankAccountNumber) {
      toast({ title: 'Missing bank information', description: 'Please provide all bank account details for ACH payments', status: 'error' });
      return;
    }

    if (!complianceAcknowledged) {
      toast({ title: 'Compliance acknowledgment required', description: 'Please acknowledge compliance with all applicable laws', status: 'error' });
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Get current user ID
      const { data: { user } } = await supabase.auth.getUser();
      const currentUserId = user?.id;

      // Generate signature image
      let sigData = null;
      let sigDataUrl = null;
      if (sigPad.current) {
        sigData = sigPad.current.getCanvas().toDataURL('image/png');
        sigDataUrl = sigData;
      }

      // Generate PDF hash (simple hash for now - in production use crypto)
      // Use a combination of timestamp and ID for hash since btoa doesn't handle Unicode
      const timestamp = new Date().toISOString();
      const pdfHash = btoa(phlebotomistInfo.id + timestamp + signDate).replace(/[^a-zA-Z0-9]/g, '').substring(0, 64);

      // Determine agreement version (you can make this dynamic)
      const agreementVersion = 'v1.0';

      // 1. Insert into agreement history
      const { data: agreementHistory, error: agreementError } = await supabase
        .from('phlebotomist_agreement_history')
        .insert({
          phlebotomist_id: phlebotomistInfo.id,
          agreement_version: agreementVersion,
          is_current: true,
          agreement_signed: true,
          agreement_signed_at: new Date().toISOString(),
          agreement_effective_date: effectiveDate,
          agreement_termination_notice_days: 30,
          agreement_company_name: companyName,
          agreement_printed_name: printedName,
          agreement_signature: sigData,
          agreement_signature_url: null, // Can upload to storage later
          per_draw_fee: parseFloat(perDrawFee),
          payment_frequency: paymentFrequency,
          payment_method: paymentMethod,
          compliance_acknowledged: complianceAcknowledged,
          compliance_acknowledged_at: new Date().toISOString(),
          baa_signed: baaSigned,
          fmv_certified: fmvCertified,
          state_addendum: stateAddendum ? [stateAddendum] : [],
          agreement_pdf_hash: pdfHash,
          comments: comments || null,
          created_by_user: currentUserId
        })
        .select()
        .single();

      if (agreementError) {
        throw agreementError;
      }

      // 2. Mark previous agreements as replaced
      if (agreementHistory?.id) {
        await supabase.rpc('mark_previous_agreement_replaced', {
          p_phlebotomist_id: phlebotomistInfo.id,
          p_new_agreement_id: agreementHistory.id
        });
      }

      // 3. Insert payment info into history (bank info always required for ACH)
      let paymentInfoHistory = null;
      if (bankAccountName && bankName && bankRoutingNumber && bankAccountNumber) {
        const { data: paymentHistory, error: paymentError } = await supabase
          .from('phlebotomist_payment_info_history')
          .insert({
            phlebotomist_id: phlebotomistInfo.id,
            agreement_history_id: agreementHistory.id,
            is_current: true,
            effective_date: effectiveDate,
            bank_account_name: bankAccountName,
            bank_name: bankName,
            bank_routing_number: bankRoutingNumber,
            bank_account_number: bankAccountNumber,
            bank_account_type: bankAccountType,
            payment_method: paymentMethod,
            payment_frequency: paymentFrequency,
            bank_verified: false,
            created_by_user: currentUserId
          })
          .select()
          .single();

        if (paymentError) {
          console.error('Error saving payment info:', paymentError);
          // Don't fail the whole process if payment info fails
        } else {
          paymentInfoHistory = paymentHistory;
          
          // Mark previous payment info as replaced
          if (paymentHistory?.id) {
            await supabase.rpc('mark_previous_payment_info_replaced', {
              p_phlebotomist_id: phlebotomistInfo.id,
              p_new_payment_info_id: paymentHistory.id
            });
          }
        }
      }

      // 4. Update phlebotomist_profiles with current references and legacy fields
      const updateData = {
        agreement_signed: true,
        agreement_signed_at: new Date().toISOString(),
        agreement_company_name: companyName,
        agreement_printed_name: printedName,
        agreement_date: signDate,
        agreement_signature: sigData,
        current_agreement_id: agreementHistory.id,
        updated_at: new Date().toISOString()
      };

      if (paymentInfoHistory?.id) {
        updateData.current_payment_info_id = paymentInfoHistory.id;
      }

      const { error: updateError } = await supabase
        .from('phlebotomist_profiles')
        .update(updateData)
        .eq('id', phlebotomistInfo.id);

      if (updateError) {
        throw updateError;
      }

      setIsSuccess(true);
      
      toast({ 
        title: 'Agreement signed successfully!', 
        description: 'Your agreement has been saved and recorded in our system.',
        status: 'success',
        duration: 3000,
        isClosable: true
      });

      // Navigate to dashboard after a short delay
      setTimeout(() => {
        navigate(`/phlebotomist/${phlebotomistInfo.id}`, { replace: true });
      }, 2000);
      
    } catch (err) {
      console.error('Error in handleSign:', err);
      toast({ 
        title: 'Error saving agreement', 
        description: err.message, 
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleManualRedirect = () => {
    navigate(`/phlebotomist/${id}`, { replace: true });
  };

  // Show success message if agreement was signed
  if (isSuccess) {
    return (
      <Box maxW="700px" mx="auto" mt={8} p={6} bg="white" borderRadius="xl" boxShadow="xl">
        <VStack spacing={6} textAlign="center">
          <Box color="green.500" fontSize="6xl">✓</Box>
          <Heading size="lg" color="green.600">Agreement Signed Successfully!</Heading>
          <Text fontSize="lg">Your business agreement has been signed and saved.</Text>
          <Text fontSize="md" color="gray.600">You should be redirected to your dashboard automatically.</Text>
          <Button 
            colorScheme="blue" 
            size="lg" 
            onClick={handleManualRedirect}
            mt={4}
          >
            Go to Dashboard
          </Button>
        </VStack>
      </Box>
    );
  }

  if (phlebotomistInfo && (phlebotomistInfo.send_agreement === 'no' || phlebotomistInfo.send_agreement === false || phlebotomistInfo.send_agreement === 0 || phlebotomistInfo.send_agreement === null)) {
    return (
      <Box maxW="700px" mx="auto" mt={8} p={6} bg="white" borderRadius="xl" boxShadow="xl" textAlign="center">
        <Heading size="lg" mb={4} color="green.600">No Agreement Required</Heading>
        <Text fontSize="lg" mb={6}>No onboarding agreement is required for this phlebotomist. You may proceed to your dashboard.</Text>
        <Button colorScheme="blue" size="lg" onClick={() => navigate(`/phlebotomist/${id}`)}>
          Go to Dashboard
        </Button>
      </Box>
    );
  }

  return (
    <Box maxW="900px" mx="auto" mt={8} p={6} bg="white" borderRadius="xl" boxShadow="xl">
      <Heading size="lg" mb={4}>Phlebotomist Service Agreement</Heading>
      
      {/* Agreement Preview */}
      <Box maxH="400px" overflowY="auto" mb={6} p={4} borderWidth="1px" borderRadius="md" bg="gray.50">
        <Text 
          fontSize="sm" 
          whiteSpace="pre-wrap" 
          lineHeight="1.8"
          color="gray.700"
        >
          {agreementText || 'Loading agreement...'}
        </Text>
      </Box>

      <VStack align="stretch" spacing={4}>
        {/* Basic Information */}
        <Box>
          <Heading size="sm" mb={3}>Basic Information</Heading>
          <HStack spacing={4}>
            <FormControl isRequired flex={1}>
              <FormLabel fontSize="sm">Company Name</FormLabel>
              <Input size="sm" value={companyName} onChange={e => setCompanyName(e.target.value)} />
            </FormControl>
            <FormControl isRequired flex={1}>
              <FormLabel fontSize="sm">Printed Name / Title</FormLabel>
              <Input size="sm" value={printedName} onChange={e => setPrintedName(e.target.value)} placeholder="Name / Title" />
            </FormControl>
          </HStack>
          <HStack spacing={4} mt={3}>
            <FormControl isRequired flex={1}>
              <FormLabel fontSize="sm">Effective Date</FormLabel>
              <Input size="sm" type="date" value={effectiveDate} onChange={e => setEffectiveDate(e.target.value)} />
            </FormControl>
            <FormControl isRequired flex={1}>
              <FormLabel fontSize="sm">Sign Date</FormLabel>
              <Input size="sm" type="date" value={signDate} onChange={e => setSignDate(e.target.value)} />
            </FormControl>
          </HStack>
        </Box>

        <Divider />

        {/* Compensation */}
        <Box>
          <Heading size="sm" mb={3}>Compensation & Payment</Heading>
          <HStack spacing={4}>
            <FormControl isRequired flex={1}>
              <FormLabel fontSize="sm">Per-Draw Fee ($)</FormLabel>
              <Input
                size="sm"
                type="number"
                step="0.01"
                min="0"
                value={perDrawFee}
                onChange={e => setPerDrawFee(e.target.value)}
                placeholder="0.00"
              />
            </FormControl>
            <FormControl isRequired flex={1}>
              <FormLabel fontSize="sm">Payment Frequency</FormLabel>
              <Input
                size="sm"
                value="Monthly"
                isReadOnly
                bg="gray.50"
                cursor="not-allowed"
              />
            </FormControl>
            <FormControl isRequired flex={1}>
              <FormLabel fontSize="sm">Payment Method</FormLabel>
              <Input
                size="sm"
                value="ACH"
                isReadOnly
                bg="gray.50"
                cursor="not-allowed"
              />
            </FormControl>
          </HStack>
        </Box>

        <Divider />

        {/* Bank Information (Required for ACH) */}
        <Box>
          <Heading size="sm" mb={3}>Bank Account Information</Heading>
            <HStack spacing={4} mb={3}>
              <FormControl isRequired flex={1}>
                <FormLabel fontSize="sm">Account Name</FormLabel>
                <Input size="sm" value={bankAccountName} onChange={e => setBankAccountName(e.target.value)} />
              </FormControl>
              <FormControl isRequired flex={1}>
                <FormLabel fontSize="sm">Bank Name</FormLabel>
                <Input size="sm" value={bankName} onChange={e => setBankName(e.target.value)} />
              </FormControl>
            </HStack>
            <HStack spacing={4}>
              <FormControl isRequired flex={1}>
                <FormLabel fontSize="sm">Routing Number</FormLabel>
                <Input size="sm" value={bankRoutingNumber} onChange={e => setBankRoutingNumber(e.target.value)} />
              </FormControl>
              <FormControl isRequired flex={1}>
                <FormLabel fontSize="sm">Account Number</FormLabel>
                <Input size="sm" type="password" value={bankAccountNumber} onChange={e => setBankAccountNumber(e.target.value)} />
              </FormControl>
              <FormControl flex={1}>
                <FormLabel fontSize="sm">Account Type</FormLabel>
                <Select size="sm" value={bankAccountType} onChange={e => setBankAccountType(e.target.value)}>
                  <option value="checking">Checking</option>
                  <option value="savings">Savings</option>
                </Select>
              </FormControl>
            </HStack>
          </Box>

        <Divider />

        {/* Compliance */}
        <Box>
          <Heading size="sm" mb={3}>Compliance & Acknowledgments</Heading>
          <VStack align="stretch" spacing={3}>
            <Checkbox 
              isChecked={complianceAcknowledged} 
              onChange={e => setComplianceAcknowledged(e.target.checked)}
              colorScheme="blue"
            >
              I acknowledge compliance with all applicable federal and state laws, including Anti-Kickback Statute, Stark Law, and HIPAA
            </Checkbox>
            <Checkbox 
              isChecked={baaSigned} 
              onChange={e => setBaaSigned(e.target.checked)}
              colorScheme="blue"
            >
              Business Associate Agreement (BAA) has been executed
            </Checkbox>
            <Checkbox 
              isChecked={fmvCertified} 
              onChange={e => setFmvCertified(e.target.checked)}
              colorScheme="blue"
            >
              Fair Market Value Certification has been provided
            </Checkbox>
            <FormControl>
              <FormLabel fontSize="sm">State Addendum (if required)</FormLabel>
              <Input 
                size="sm" 
                value={stateAddendum} 
                onChange={e => setStateAddendum(e.target.value)} 
                placeholder="Enter state code (e.g., CA, NY)"
              />
            </FormControl>
          </VStack>
        </Box>

        <Divider />

        {/* Signature */}
        <Box>
          <Heading size="sm" mb={3}>Signature</Heading>
          <HStack spacing={4} alignItems="flex-start">
            <Box flex={2}>
              <FormLabel fontSize="sm" mb={2}>Electronic Signature</FormLabel>
              <Box borderWidth="2px" borderRadius="md" p={2} bg="white">
                <SignatureCanvas
                  penColor="black"
                  canvasProps={{ width: 500, height: 100, className: 'sigCanvas' }}
                  ref={sigPad}
                  onEnd={() => setIsSigned(!sigPad.current.isEmpty())}
                  onClear={() => setIsSigned(false)}
                />
              </Box>
              <Button mt={2} size="xs" onClick={() => sigPad.current && sigPad.current.clear()}>
                Clear Signature
              </Button>
            </Box>
          </HStack>
        </Box>

        {/* Additional Comments */}
        <FormControl>
          <FormLabel fontSize="sm">Additional Comments (Optional)</FormLabel>
          <Textarea 
            size="sm" 
            value={comments} 
            onChange={e => setComments(e.target.value)} 
            placeholder="Any additional notes or comments..."
            rows={3}
          />
        </FormControl>

        {/* Validation Messages */}
        {(!companyName || !printedName || !signDate || !effectiveDate || !isSigned || !perDrawFee || !complianceAcknowledged) && (
          <Alert status="warning" size="sm">
            <AlertIcon />
            Please fill all required fields, enter a valid per-draw fee, acknowledge compliance, and provide your signature.
          </Alert>
        )}

        {/* Submit Button */}
        <Button
          colorScheme="blue"
          size="lg"
          width="100%"
          onClick={handleSign}
          isDisabled={
            !companyName ||
            !printedName ||
            !signDate ||
            !effectiveDate ||
            !isSigned ||
            !perDrawFee ||
            parseFloat(perDrawFee) <= 0 ||
            !complianceAcknowledged ||
            !bankAccountName ||
            !bankName ||
            !bankRoutingNumber ||
            !bankAccountNumber ||
            isSubmitting
          }
          isLoading={isSubmitting}
          loadingText="Saving Agreement..."
        >
          {isSubmitting ? 'Saving...' : 'Sign & Complete Agreement'}
        </Button>
      </VStack>
    </Box>
  );
}
